import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { COLLECTIONS, HERO_IMAGE } from "@/data/products";
import { useMedia } from "@/context/MediaContext";

const ORDER = ["for-him", "for-her", "unisex"];
const EASE = [0.22, 1, 0.36, 1];

export default function HeroSlider() {
  const img = useMedia();
  const slides = [
    {
      key: "brand",
      eyebrow: "Reserve 30 Parfums",
      title: "Crafted To Leave A Mark.",
      sub: "Luxury fragrances at parfum strength, matured 8–10 weeks.",
      cta: "Shop the collection",
      to: "/collections",
      image: img("hero_banner", HERO_IMAGE),
    },
    ...ORDER.map((slug) => {
      const c = COLLECTIONS[slug];
      return { key: slug, eyebrow: c.tagline, title: c.title, sub: c.description, cta: "Discover the collection", to: `/collections/${slug}`, image: img(`collection_${slug.replace("-", "_")}`, c.image) };
    }),
  ];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((n) => setI((p) => (p + n + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => go(1), 6500);
    return () => clearInterval(t);
  }, [go, paused]);

  const s = slides[i];

  return (
    <section
      data-testid="hero-slider"
      className="relative h-[100svh] min-h-[560px] bg-[#111111] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence initial={false}>
        <motion.img
          key={s.key}
          src={s.image}
          alt={s.title}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover object-[50%_25%] md:object-center"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10 md:from-black/75 md:via-black/25 md:to-black/15" />

      <div className="relative h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28 text-[#F7F3EE]">
        <AnimatePresence mode="wait">
          <motion.div
            key={s.key}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.8, ease: EASE }}
            data-testid={`hero-slide-${s.key}`}
          >
            <p className="overline-label truncate">{s.eyebrow}</p>
            <h1 className="font-serif-brand font-light text-5xl sm:text-6xl lg:text-8xl mt-4 max-w-3xl leading-[0.95]">{s.title}</h1>
            <p className="mt-5 max-w-md text-sm md:text-base text-[#F7F3EE]/75 leading-relaxed">{s.sub}</p>
            <Link to={s.to} data-testid={`hero-slide-cta-${s.key}`} className="btn-ghost-ivory mt-8">
              {s.cta}
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-8 left-5 md:left-10 right-5 md:right-10 flex items-center justify-between text-[#F7F3EE]">
        <div className="flex items-center gap-3" data-testid="hero-slider-dots">
          {slides.map((sl, idx) => (
            <button
              key={sl.key}
              type="button"
              onClick={() => setI(idx)}
              data-testid={`hero-slider-dot-${idx}`}
              aria-label={`Go to slide ${idx + 1}`}
              className="py-3 flex items-center"
            >
              <span className={`block h-px transition-all duration-500 ${idx === i ? "w-12 bg-[#BA8D53]" : "w-6 bg-[#F7F3EE]/40 hover:bg-[#F7F3EE]/80"}`} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[0.62rem] tracking-[0.3em] mr-3 text-[#F7F3EE]/70">
            {String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
          <button type="button" onClick={() => go(-1)} data-testid="hero-slider-prev" aria-label="Previous slide" className="h-10 w-10 border border-[#F7F3EE]/30 flex items-center justify-center hover:bg-[#F7F3EE] hover:text-[#111] transition-colors duration-300">
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button type="button" onClick={() => go(1)} data-testid="hero-slider-next" aria-label="Next slide" className="h-10 w-10 border border-[#F7F3EE]/30 flex items-center justify-center hover:bg-[#F7F3EE] hover:text-[#111] transition-colors duration-300">
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}
